import { useEffect, useState } from 'react';
import type {
  EntitlementMatrixRow,
  EffectiveEntitlement,
  EntitlementImpactPreview,
  DriftCase,
} from '@hospasense/contracts';
import { useAuth } from '../lib/auth.js';
import {
  fetchEntitlementMatrix,
  previewEntitlementImpact,
  addEntitlementOverride,
  fetchDriftCases,
  reconcileDriftCase,
  fetchOrganisationsList,
} from '../lib/queries-commercial.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Button,
  Card,
  CardBody,
  PageHeader,
  Badge,
  StatusPill,
  Modal,
  Input,
  Select,
  Textarea,
  Table,
  TableContainer,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingState,
  ErrorState,
  PermissionDeniedState,
  SearchableSelect,
  useToast,
} from '../components/ui/index.js';

export function EntitlementsPage() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const allowed = hasPermission('entitlement.read');
  const [organisations, setOrganisations] = useState<Array<{ id: string; displayName: string }>>(
    [],
  );
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [matrixRows, setMatrixRows] = useState<EntitlementMatrixRow[]>([]);
  const [effective, setEffective] = useState<EffectiveEntitlement | null>(null);
  const [driftCases, setDriftCases] = useState<DriftCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Impact preview state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [impactPreview, setImpactPreview] = useState<EntitlementImpactPreview | null>(null);

  // Waiver override modal state
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [waiverFeature, setWaiverFeature] = useState('abdm_bridge');
  const [waiverReason, setWaiverReason] = useState('30-day state registry pilot');

  // Reconcile drift modal state
  const [selectedDriftCase, setSelectedDriftCase] = useState<DriftCase | null>(null);
  const [reconcileNote, setReconcileNote] = useState('');

  const loadOrgs = async () => {
    try {
      const items = await fetchOrganisationsList();
      setOrganisations(items);
      if (items.length > 0) {
        setSelectedOrgId(items[0]!.id);
        await loadMatrixAndDrift(items[0]!.id);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const loadMatrixAndDrift = async (orgId: string) => {
    if (!orgId) return;
    try {
      setLoading(true);
      const [matrixData, driftData] = await Promise.all([
        fetchEntitlementMatrix(orgId),
        fetchDriftCases({ organisationId: orgId }),
      ]);
      setMatrixRows(matrixData.rows);
      setEffective(matrixData.effective);
      setDriftCases(driftData.items);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) {
      void loadOrgs();
    }
  }, [allowed]);

  useEffect(() => {
    if (selectedOrgId && allowed) {
      void loadMatrixAndDrift(selectedOrgId);
    }
  }, [selectedOrgId, allowed]);

  if (!allowed) return <PermissionDeniedState what="Entitlements & Drift" />;

  const handleImpactPreview = async () => {
    if (!selectedOrgId || !effective) return;
    try {
      const preview = await previewEntitlementImpact(selectedOrgId, ['billing_invoicing'], {
        licensed_beds: 50,
        active_doctors: 10,
        monthly_opd_visits: 2000,
        storage_gb: 100,
        connected_devices: 5,
      });
      setImpactPreview(preview);
      setShowPreviewModal(true);
    } catch (err) {
      toast.error(`Impact preview failed: ${(err as Error).message}`);
    }
  };

  const handleAddWaiver = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrgId) return;
    try {
      await addEntitlementOverride(selectedOrgId, {
        featureOrMetric: waiverFeature,
        grantType: 'waiver',
        value: true,
        reason: waiverReason,
        expiresAt: new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString(),
      });
      setShowWaiverModal(false);
      await loadMatrixAndDrift(selectedOrgId);
    } catch (err) {
      toast.error(`Waiver grant failed: ${(err as Error).message}`);
    }
  };

  const handleReconcileDrift = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDriftCase) return;
    try {
      await reconcileDriftCase(selectedDriftCase.id, reconcileNote);
      setSelectedDriftCase(null);
      setReconcileNote('');
      await loadMatrixAndDrift(selectedOrgId);
    } catch (err) {
      toast.error(`Drift reconciliation failed: ${(err as Error).message}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="entitlements-page">
      <PageHeader
        title="Entitlements & Drift Reconciliation"
        subtitle="4-Tier software entitlement matrix, telemetry drift queue, and impact analysis"
        breadcrumbs={
          <Breadcrumbs items={[{ label: 'Control', to: '/' }, { label: 'Entitlements' }]} />
        }
        actions={
          <div className="flex items-center gap-3">
            <div className="w-56">
              <SearchableSelect
                label=""
                placeholder="Search client…"
                value={selectedOrgId}
                onChange={(val) => setSelectedOrgId(val)}
                options={organisations.map((o) => ({
                  value: o.id,
                  label: o.displayName,
                }))}
              />
            </div>

            <Button
              variant="secondary"
              onClick={handleImpactPreview}
              data-testid="preview-impact-button"
            >
              Impact Preview
            </Button>

            {hasPermission('entitlement.manage') && (
              <Button
                variant="primary"
                onClick={() => setShowWaiverModal(true)}
                data-testid="grant-waiver-button"
              >
                + Grant Override
              </Button>
            )}
          </div>
        }
      />

      {error && (
        <ErrorState description={error} onRetry={() => loadMatrixAndDrift(selectedOrgId)} />
      )}

      {/* Patient Safety Policy Banner */}
      <Card className="border-border bg-surface-raised">
        <CardBody className="flex items-start gap-3 text-xs">
          <span className="text-base">ℹ️</span>
          <div>
            <h3 className="font-semibold text-foreground">Patient Safety Guarantee</h3>
            <p className="text-muted-foreground mt-0.5">
              Capacity overages and billing grace periods never terminate active clinical care
              workflows or hard-block doctor encounters. Overages trigger commercial drift
              reconciliation alerts without hospital outage.
            </p>
          </div>
        </CardBody>
      </Card>

      {/* 4-Tier Entitlement Matrix */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <h2 className="text-base font-semibold text-foreground">4-Tier Entitlement Matrix</h2>
          {effective?.inGracePeriod && (
            <Badge tone="warning">
              In Grace Period until{' '}
              {effective.graceUntil ? new Date(effective.graceUntil).toLocaleDateString() : 'N/A'}
            </Badge>
          )}
        </div>

        <TableContainer>
          <Table data-testid="entitlement-matrix-table">
            <THead>
              <TR>
                <TH>Feature / Metric</TH>
                <TH>Type</TH>
                <TH>1. Contracted</TH>
                <TH>2. Effective</TH>
                <TH>3. Provisioned</TH>
                <TH>4. Telemetry Usage</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {loading ? (
                <TR>
                  <TD colSpan={7} className="text-center py-6">
                    <LoadingState label="Loading matrix…" />
                  </TD>
                </TR>
              ) : (
                matrixRows.map((r) => (
                  <TR key={r.key}>
                    <TD className="font-medium text-foreground">{r.label}</TD>
                    <TD className="text-xs uppercase text-muted-foreground">{r.type}</TD>
                    <TD>
                      {typeof r.contracted === 'boolean'
                        ? r.contracted
                          ? 'Yes'
                          : '—'
                        : r.contracted}
                    </TD>
                    <TD className="font-semibold text-foreground">
                      {typeof r.effective === 'boolean'
                        ? r.effective
                          ? 'Enabled'
                          : '—'
                        : r.effective}
                    </TD>
                    <TD>
                      {r.provisioned === null
                        ? '—'
                        : typeof r.provisioned === 'boolean'
                          ? r.provisioned
                            ? 'Active'
                            : 'Inactive'
                          : r.provisioned}
                    </TD>
                    <TD className="font-mono text-xs">
                      {r.observedUsage === null ? '—' : r.observedUsage}
                    </TD>
                    <TD>
                      {r.driftDetected ? (
                        <StatusPill tone="warning" label="Drift Alert" />
                      ) : (
                        <StatusPill tone="success" label="Synced" />
                      )}
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </TableContainer>
      </div>

      {/* Drift Cases Queue */}
      <div className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">Reconciliation & Drift Cases</h2>
        <TableContainer>
          <Table data-testid="drift-cases-table">
            <THead>
              <TR>
                <TH>Title</TH>
                <TH>Severity</TH>
                <TH>Status</TH>
                <TH>Detected</TH>
                <TH>Action</TH>
              </TR>
            </THead>
            <TBody>
              {driftCases.length === 0 ? (
                <TR>
                  <TD colSpan={5} className="text-center text-muted-foreground py-6">
                    No active drift cases for this organization
                  </TD>
                </TR>
              ) : (
                driftCases.map((d) => (
                  <TR key={d.id}>
                    <TD className="font-medium text-foreground">
                      <div>{d.title}</div>
                      <div className="text-xs text-muted-foreground">{d.description}</div>
                    </TD>
                    <TD>
                      <Badge
                        tone={
                          d.severity === 'critical'
                            ? 'error'
                            : d.severity === 'warning'
                              ? 'warning'
                              : 'info'
                        }
                      >
                        {d.severity}
                      </Badge>
                    </TD>
                    <TD>
                      <StatusPill
                        tone={d.status === 'open' ? 'error' : 'success'}
                        label={d.status}
                      />
                    </TD>
                    <TD className="text-xs">{new Date(d.detectedAt).toLocaleDateString()}</TD>
                    <TD>
                      {d.status === 'open' && hasPermission('entitlement.reconcile') && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setSelectedDriftCase(d);
                            setReconcileNote('');
                          }}
                          data-testid={`reconcile-drift-${d.id}`}
                        >
                          Reconcile
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </TableContainer>
      </div>

      {/* Impact Preview Modal */}
      {showPreviewModal && impactPreview && (
        <Modal
          open={showPreviewModal}
          title="Entitlement Impact Preview"
          onClose={() => setShowPreviewModal(false)}
        >
          <div className="space-y-4" data-testid="impact-preview-modal">
            {impactPreview.patientSafetyRisk && (
              <Card className="border-border bg-surface-raised">
                <CardBody className="text-xs space-y-1">
                  <div className="font-bold text-foreground">PATIENT SAFETY IMPACT DETECTED</div>
                  <div className="text-muted-foreground">{impactPreview.safetyConsequences}</div>
                  <div className="font-semibold text-foreground">
                    Requires two-person approval and Step-up MFA verification.
                  </div>
                </CardBody>
              </Card>
            )}

            <div className="space-y-2 text-xs">
              <div>
                <span className="font-semibold block text-foreground">Modules to Remove:</span>
                <span className="font-mono text-muted-foreground">
                  {impactPreview.modulesToRemove.join(', ') || 'None'}
                </span>
              </div>
              <div>
                <span className="font-semibold block text-foreground">
                  Estimated Affected Users:
                </span>
                <span className="text-muted-foreground">
                  {impactPreview.estimatedAffectedUsers} clinicians and staff
                </span>
              </div>
              <div>
                <span className="font-semibold block text-foreground">Rollback Strategy:</span>
                <span className="text-muted-foreground">{impactPreview.suggestedRollbackPlan}</span>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
                Close Preview
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Grant Override Modal */}
      {showWaiverModal && (
        <Modal
          open={showWaiverModal}
          title="Grant Temporary Override / Waiver"
          onClose={() => setShowWaiverModal(false)}
        >
          <form onSubmit={handleAddWaiver} className="space-y-3">
            <Select
              label="Feature / Module"
              value={waiverFeature}
              onChange={(e) => setWaiverFeature(e.target.value)}
            >
              <option value="abdm_bridge">ABDM Bridge (National Health Registry)</option>
              <option value="patient_portal">Patient Portal</option>
              <option value="analytics_reports">Analytics & Reports</option>
              <option value="lab_radiology">Lab & Radiology</option>
            </Select>

            <Input
              label="Reason / Justification"
              value={waiverReason}
              onChange={(e) => setWaiverReason(e.target.value)}
              required
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowWaiverModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Grant Waiver
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Reconcile Drift Modal */}
      {selectedDriftCase && (
        <Modal
          open={Boolean(selectedDriftCase)}
          title="Reconcile Drift Case"
          onClose={() => setSelectedDriftCase(null)}
        >
          <form onSubmit={handleReconcileDrift} className="space-y-3">
            <p className="text-xs text-muted-foreground">{selectedDriftCase.title}</p>
            <Textarea
              label="Resolution Note"
              value={reconcileNote}
              onChange={(e) => setReconcileNote(e.target.value)}
              placeholder="e.g. Add-on amendment signed; increased bed capacity limit."
              required
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setSelectedDriftCase(null)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Confirm Resolution
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
